import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Observable } from 'rxjs';
import { ExpandedSummary } from '../../../../report-data/models/expanded-summary.model';
import { ReportDataStore } from '../../../../report-data/report-data.store';
import { ReportExpandedViewTypesEnum } from '../../../../report-data/report-data.data';
import { filter, map, shareReplay } from 'rxjs/operators';
import { AlignmentEnum, I2gDialogService, IRouter } from 'i2g-ng-ui';
import { ExpandedViewItem } from '../../../../report-data/models/sub-models/expanded-view-item.model';
import { List } from 'immutable';
import { LocalizationService } from '../../../../localization/localization.service';
import { TrackingScreenNamesEnum } from '../../../../tracking/tracking.data';
import {
  I2gNgTrackingActionBuilder,
  I2gNgTrackingService,
  ScrollDirectionEnum,
} from 'i2g-ng-tracking';
import { ReportExpandedTrackingObject } from '../../../../tracking/models/objects/report-expanded-module.model';
import { debounce } from '../../../../shared/decorators/debounce.decorator';
import { HomeDataDispatcher } from '../../../../home-data';
import { InsightsSummaryBuilder } from '../../../builders';

@Component({
  selector: 'app-overdue-invoices-expanded-dialog',
  templateUrl: './overdue-invoices-expanded-dialog.component.html',
  styleUrls: ['./overdue-invoices-expanded-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [InsightsSummaryBuilder],
})
export class OverdueInvoicesExpandedDialogComponent
  implements OnInit, AfterViewInit {
  @Input() trackingObject: ReportExpandedTrackingObject;
  @Input() trackingScreenName: TrackingScreenNamesEnum;

  private _previousScroll: number;

  reportType: ReportExpandedViewTypesEnum = ReportExpandedViewTypesEnum.overdue;

  alignmentEnum: typeof AlignmentEnum;
  locale: string;
  showEmptyState$: Observable<boolean>;
  emptyStateCta$: Observable<IRouter>;

  summaryData$: Observable<ExpandedSummary>;
  summaryDataItems$: Observable<List<ExpandedViewItem>>;
  dialogCtaRoute$: Observable<IRouter>;

  @ViewChild('scrollElement', { static: false }) private _scrollElement;

  constructor(
    private _reportDataStore: ReportDataStore,
    private _dialogService: I2gDialogService,
    private _localizationService: LocalizationService,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _trackingService: I2gNgTrackingService,
    private _homeDataDispatcher: HomeDataDispatcher,
    private _insightsSummaryBuilder: InsightsSummaryBuilder
  ) {}

  ngOnInit() {
    this.alignmentEnum = AlignmentEnum;
    this.locale = this._localizationService.getLocale();

    this.summaryData$ = this._reportDataStore
      .getExpandedSummaryDataByType(this.reportType)
      .pipe(
        filter((x) => !!x),
        shareReplay(1)
      );

    this.summaryDataItems$ = this._reportDataStore.getExpandedViewItemsByType(
      this.reportType
    );

    this.dialogCtaRoute$ = this.summaryData$.pipe(
      map((summaryData) =>
        this._insightsSummaryBuilder.buildSummaryCtaRoute(
          summaryData,
          this.trackingScreenName
        )
      )
    );

    /**
     * Empty state
     */
    this.showEmptyState$ = this.summaryData$.pipe(
      map((summaryData) =>
        this._insightsSummaryBuilder.isEmptySummary(summaryData)
      )
    );

    this.emptyStateCta$ = this.summaryData$.pipe(
      map((summaryData) =>
        this._insightsSummaryBuilder.buildEmptyStateCtaRoute(
          summaryData,
          this.trackingScreenName
        )
      )
    );
  }

  ngAfterViewInit() {
    this._previousScroll = this._scrollElement
      ? this._scrollElement.nativeElement.scrollTop
      : 0;
  }

  handleDeeplink(deeplink: string) {
    if (!deeplink) {
      return false;
    }

    this._dialogService.closeDialog();

    this._homeDataDispatcher.handleInsightsHubDeeplink(
      deeplink,
      this.reportType,
      this.trackingScreenName
    );
  }

  @debounce(250)
  scrollTrackingHandler(e) {
    const scrollElement = e.target;
    const scrollDirection =
      this._previousScroll > scrollElement.scrollTop
        ? ScrollDirectionEnum.up
        : ScrollDirectionEnum.down;

    this._trackingService.trackEvent(
      this.trackingObject,
      this._trackingActionBuilder.buildScrolled(
        scrollDirection,
        this.trackingScreenName
      )
    );

    this._previousScroll = this._scrollElement.nativeElement.scrollTop;
  }

  closeDialog() {
    this._dialogService.closeDialog();
  }
}
